<template>
  <BaseDialog
    :open="open"
    :disabled="loading"
    max-width="960px"
    @close="onClose"
  >
    <template #title>
      <span>Gruppen zuweisen</span>
      <span v-if="student">
        — <strong>{{ student.last_name }}, {{ student.first_name }}</strong>
        <span
          v-if="student.student_number"
          class="muted"
        > · {{ student.student_number }}</span>
      </span>
    </template>

    <div
      class="form"
      style="display:grid;grid-template-columns:1fr 1fr;gap:12px;"
    >
      <!-- Pool -->
      <div class="field">
        <label>Verfügbare Gruppen</label>
        <div
          class="chip-box"
          style="margin-top:8px;"
        >
          <div
            class="chips"
            style="display:flex;flex-wrap:wrap;gap:6px;max-height:300px;overflow:auto;"
          >
            <button
              v-for="g in poolGroups"
              :key="g.id"
              type="button"
              :class="['chip', g.is_private ? 'warn' : '']"
              :disabled="loading"
              title="Zuweisen"
              @click="addToStudent(g.id)"
            >
              {{ g.name }}<template v-if="g.is_private">
                (privat)
              </template>
            </button>
            <p
              v-if="poolGroups.length===0"
              class="muted"
            >
              Keine verfügbaren Gruppen.
            </p>
          </div>
        </div>
      </div>

      <!-- Zugewiesen -->
      <div class="field">
        <label>Zugewiesene Gruppen</label>
        <div
          class="chip-box"
          style="margin-top:8px;"
        >
          <div
            class="chips"
            style="display:flex;flex-wrap:wrap;gap:6px;max-height:300px;overflow:auto;"
          >
            <button
              v-for="g in assignedGroups"
              :key="g.id"
              type="button"
              :class="['chip', 'active', g.is_private ? 'warn' : '']"
              :disabled="loading"
              title="Entfernen"
              @click="removeFromStudent(g.id)"
            >
              {{ g.name }}<template v-if="g.is_private">
                (privat)
              </template>
            </button>
            <p
              v-if="assignedGroups.length===0"
              class="muted"
            >
              Noch keine Gruppen zugewiesen.
            </p>
          </div>
        </div>
      </div>
    </div>

    <p
      v-if="errorMsg"
      class="notice notice-danger mt-12"
      role="alert"
    >
      {{ errorMsg }}
    </p>

    <div
      class="mt-12"
      style="display:flex;gap:8px;justify-content:flex-end;"
    >
      <button
        class="btn"
        type="button"
        :disabled="loading"
        @click="onClose"
      >
        Schließen
      </button>
    </div>
  </BaseDialog>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import BaseDialog from '@/components/BaseDialog.vue';
import api from '@/services/api';

const props = defineProps({
  open: { type: Boolean, default: false },
  student: { type: Object, default: null }
});
const emit = defineEmits(['close', 'applied']);

const loading = ref(false);
const errorMsg = ref('');

// Datenquellen
const allGroups = ref([]);     // alle Gruppen
const assignedIds = ref(new Set()); // Set<number> der zugewiesenen Gruppen

// Nur aktive Gruppen im Pool anzeigen; Zugewiesene bleiben sichtbar (auch wenn inaktiv), um Entfernen zu ermöglichen
const poolGroups = computed(() => allGroups.value.filter(g => (g?.status === true) && !assignedIds.value.has(g.id)));
const assignedGroups = computed(() => allGroups.value.filter(g => assignedIds.value.has(g.id)));

async function loadData() {
  if (!props.student?.id) return;
  loading.value = true;
  errorMsg.value = '';
  try {
    const [{data: gl}, {data: al}] = await Promise.all([
      api.get('/groups', { params: { page: 1, limit: 500, withCounts: false } }),
      api.get(`/students/${props.student.id}/groups`)
    ]);
    allGroups.value = Array.isArray(gl?.items) ? gl.items : [];
    const ids = new Set((Array.isArray(al?.items) ? al.items : []).map(g => Number(g.id)));
    assignedIds.value = ids;
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Laden fehlgeschlagen.';
    allGroups.value = [];
    assignedIds.value = new Set();
  } finally {
    loading.value = false;
  }
}

watch(() => props.open, (val) => {
  if (val) {
    loadData();
  } else {
    allGroups.value = [];
    assignedIds.value = new Set();
    errorMsg.value = '';
  }
});

function onClose() {
  emit('close');
}

async function addToStudent(groupId) {
  if (!props.student?.id) return;
  loading.value = true;
  try {
    await api.post(`/groups/${groupId}/members`, { studentId: Number(props.student.id) });
    const s = new Set(assignedIds.value); s.add(Number(groupId)); assignedIds.value = s;
    emit('applied');
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Zuweisen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

async function removeFromStudent(groupId) {
  if (!props.student?.id) return;
  loading.value = true;
  try {
    await api.delete(`/groups/${groupId}/members/${Number(props.student.id)}`);
    const s = new Set(assignedIds.value); s.delete(Number(groupId)); assignedIds.value = s;
    emit('applied');
  } catch (e) {
    errorMsg.value = e?.response?.data?.error?.message || 'Entfernen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  if (props.open) loadData();
});
</script>

<style scoped>
.chip-box {
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  padding: 8px;
}

.chip {
  border: 1px solid var(--border);
  background: var(--surface);
  color: var(--text);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 13px;
  line-height: 1.4;
}
.chip.active {
  background: rgba(79,70,229,0.12);
  border-color: var(--primary);
  color: var(--primary-600);
}

/* Warn-Variante für private Gruppen */
.chip.warn {
  background: rgba(245, 158, 11, 0.12); /* bg-warn Tönung */
  border-color: rgba(245, 158, 11, 0.5);
  color: var(--warn);
}
.chip.active.warn {
  background: rgba(245, 158, 11, 0.12); /* Warn-Variante soll 'active' überschreiben */
  border-color: rgba(245, 158, 11, 0.5);
  color: var(--warn);
}

.muted { color: var(--muted); }
</style>
